import React, { useEffect, useRef, useState } from "react";
import { Row, Form, Alert, Col, Table } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { ReduxState } from "../../../../../redux";
import { LoadingTrackedButton } from "../../../../Docuclipper/TrackedButton";
import DateFormatOptions from "./DateFormatOptions";
import { downloadSpreadsheetHelper } from "./utils";
import { groupBy } from "lodash";
import CsvColumnNames from "./CsvColumnNames";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { convertExternalFiltersToTableFilterConfig } from "src/utils/filters";
import { setIncludeStatementSummary } from "../../../../../redux/reducers/DownloadOptions";

type Props = {
  cta?: string;
};

interface PreviewTable {
  columns: string[];
  rows: (string | number)[][]; // Array of arrays of data that can be strings or numbers
  // Present for trial users: rows past `limit` arrive with text columns
  // already redacted server-side; the table blurs those cells on top.
  trial?: { limit: number; totalTransactions: number | null } | null;
}

const PREVIEW_PAGE_SIZE = 10;
// Must match TRIAL_LOCKED_PLACEHOLDER in api src/handlers/jobs-handler.ts —
// cells with this value are server-redacted and get the blur treatment.
const TRIAL_LOCKED_CELL = "Locked — upgrade to view";

export const CsvOptions: React.FC<Props> = ({ cta = "Download" }) => {
  const dispatch = useDispatch();

  const [loading, setLoading] = useState(false);
  const [separateFilesForAccounts, setSeparateFilesForAccounts] =
    useState(false);
  const [splitMultilineDescription, setSplitMultilineDescription] =
    useState(false);
  const [fileType, setFileType] = useState("CSV");
  const [previewTable, setPreviewTable] = useState<PreviewTable>({
    columns: [],
    rows: [],
  });
  const [previewPage, setPreviewPage] = useState(1);

  const { selectedExportType, options, includeStatementSummary } = useSelector(
    (state: ReduxState) => state.DownloadOptions
  );

  const { formatsByType, loadingByType } = useSelector(
    (state: ReduxState) => state.CustomFormat
  );
  const bankFormats = formatsByType.bankStatement;
  const customFormatsLoading = loadingByType.bankStatement;

  // A selectedExportType of `custom-<x>` is only safe to submit once the bank
  // format list has finished loading AND <x> resolves to a known format (or is the
  // built-in `all`/`basic`). Otherwise the backend rejects the export with
  // INVALID_OUTPUT_FORMAT (errorCode 1076) and pumps the exportJob alarm. The
  // self-heal in ExportTypeDropdown fixes the Redux state, but its dispatch is
  // async; gate the submit/preview here so a same-tick click can't race it.
  const isCustomFormatStale =
    typeof selectedExportType === "string" &&
    selectedExportType.startsWith("custom-") &&
    selectedExportType !== "custom-all" &&
    selectedExportType !== "custom-basic" &&
    (customFormatsLoading ||
      !bankFormats.some((cf) => `custom-${cf.name}` === selectedExportType));

  const { job } = useSelector((state: ReduxState) => state.JobData);
  const { downloadOptions } = useSelector(
    (state: ReduxState) => state.TransactionManager
  );
  const { T, T2, T3, T4 } = useSelector(
    (state: ReduxState) => state.GenericOptions
  );
  const { externalFilterSortBy, externalFilterSortDirection, externalFilters } =
    useSelector((state: ReduxState) => state.ExternalFilter);

  const { allAccounts } = useSelector((state: ReduxState) => state.Reconciler);
  const isColumnConfigModalOpen = useSelector(
    (state: ReduxState) => state.ColumnConfig.isModalOpen
  );
  const prevColumnConfigModalOpenRef = useRef(false);
  // const allAccounts = Object.keys(groupBy(tls.tls || [], "account"));

  const { selectedDocumentIds, selectedFieldNames } = downloadOptions;
  const { selectedL1: l1, selectedL2: l2 } = useSelector(
    (state: ReduxState) => state.TagLevel
  );
  const onExport = ({ preview = false }) => {
    setLoading(true);
    return downloadSpreadsheetHelper(
      job,
      l1,
      l2,
      "FieldsToExcel1",
      selectedExportType,
      {
        dateFormat: options[`docuclipper.prefs.dateFormat`] || "",
        T,
        T2,
        T3,
        T4,
        separateFilesForAccounts,
        fileType,
        splitMultilineDescription,
        includeStatementSummary,
      },
      selectedDocumentIds,
      selectedFieldNames,
      [],
      [
        ...convertExternalFiltersToTableFilterConfig(externalFilters),
        {
          field: "fieldName",
          operator: "equals",
          value: "bankMode",
        },
      ],
      externalFilterSortBy,
      externalFilterSortDirection,
      fileType as "CSV" | "XLSX",
      preview
    )
      .then((data) => {
        if (preview) {
          setPreviewTable({
            columns: data.columns,
            rows: data.rows,
            trial: data.trial || null,
          });
          setPreviewPage(1);
        }
        setLoading(false);
      })
      .catch((err) => setLoading(false));
  };

  if (fileType === "XLSX") {
    cta = cta?.replace("CSV", "XLSX");
  }

  // The preview is rendered by the same backend export path, which applies the
  // chosen date format — so refresh it when the user changes the format too.
  const dateFormatPref = options[`docuclipper.prefs.dateFormat`];

  useEffect(() => {
    // console.log({ selectedExportType });
    if (selectedExportType !== "xlsx" && !isCustomFormatStale) {
      onExport({
        preview: true,
      });
    }
  }, [
    selectedExportType,
    selectedDocumentIds,
    isCustomFormatStale,
    dateFormatPref,
  ]);

  useEffect(() => {
    const wasOpen = prevColumnConfigModalOpenRef.current;
    const isClosedNow = !isColumnConfigModalOpen;

    if (
      wasOpen &&
      isClosedNow &&
      selectedExportType !== "xlsx" &&
      !isCustomFormatStale
    ) {
      onExport({ preview: true });
    }

    prevColumnConfigModalOpenRef.current = isColumnConfigModalOpen;
  }, [isColumnConfigModalOpen, selectedExportType, isCustomFormatStale]);

  const totalRows = previewTable.rows.length;
  const totalPreviewPages = Math.max(1, Math.ceil(totalRows / PREVIEW_PAGE_SIZE));
  const previewRangeStart = totalRows === 0 ? 0 : (previewPage - 1) * PREVIEW_PAGE_SIZE + 1;
  const previewRangeEnd = Math.min(previewPage * PREVIEW_PAGE_SIZE, totalRows);

  return (
    <div>
      {(selectedExportType === "csv" ||
        selectedExportType?.startsWith("custom-")) && (
        <Row>
          <Col className="my-2">
            <strong>File Type</strong>
            <div className="d-flex">
              <Form.Check
                value="CSV"
                checked={fileType === "CSV"}
                onChange={(e) => setFileType(e.currentTarget.value)}
                type="radio"
                label="CSV"
              />
              <Form.Check
                className="ml-2"
                value="XLSX"
                checked={fileType === "XLSX"}
                onChange={(e) => setFileType(e.currentTarget.value)}
                type="radio"
                label="XLSX"
              />
            </div>
          </Col>
          <Col className="my-2">
            {(selectedExportType === "csv" ||
              selectedExportType?.startsWith("custom-")) && (
              <div>
                <div className="font-weight-bold">Date Format</div>
                <DateFormatOptions />
              </div>
            )}
          </Col>
        </Row>
      )}
      {([
        "csv",
        "sage-csv",
        "xero-csv",
        "sage-cloud-csv",
        "myob",
        "relate",
        "netsuite",
      ].includes(selectedExportType) ||
        selectedExportType?.startsWith("custom-")) && (
        <CsvColumnNames selectedExportType={selectedExportType} />
      )}
      {(selectedExportType === "csv" ||
        selectedExportType?.startsWith("custom-")) && (
        <>
          <strong>Advanced Options</strong>
          <Form.Group className="mx-1" controlId="splitMultilineDescription">
            <Form.Check
              checked={splitMultilineDescription}
              onChange={(e) => setSplitMultilineDescription(e.target.checked)}
              type="checkbox"
              label="Split multiline descriptions into separate columns"
            />
          </Form.Group>
          <Form.Group className="mx-1" controlId="includeStatementSummary">
            <Form.Check
              checked={includeStatementSummary}
              onChange={(e) =>
                dispatch(setIncludeStatementSummary(e.target.checked))
              }
              type="checkbox"
              label="Include statement summary (period dates + balances)"
            />
          </Form.Group>
        </>
      )}

      {false &&
        (selectedExportType === "csv" ||
          selectedExportType?.startsWith("custom-")) &&
        allAccounts.length > 1 && (
          <>
            {separateFilesForAccounts && (
              <Alert variant="info" className="mt-2">
                <>
                  <span className="mr-2">
                    {`DocuClipper has detected multiple accounts and will download
                    a zip file containing separate files for each account`}
                  </span>
                  <a
                    href="https://www.docuclipper.com/docs/docuclipper-exports-data-in-zip-file/"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <FontAwesomeIcon icon="question-circle" />
                    <span className="ml-1">See instructions</span>
                    <FontAwesomeIcon
                      className="ml-2"
                      icon="external-link-alt"
                    />
                  </a>
                </>
              </Alert>
            )}
            <Form.Group className="mx-1 my-3" controlId="oneFilePerAccount">
              <Form.Check
                checked={separateFilesForAccounts}
                onChange={(e) => setSeparateFilesForAccounts(e.target.checked)}
                type="checkbox"
                label="Download separate files for each account"
              />
            </Form.Group>
          </>
        )}

      {selectedExportType !== "qif" &&
        previewTable.columns?.length > 0 &&
        previewTable.rows?.length > 0 && (
          <>
            <strong>Preview</strong>
            <span className="text-muted ml-2" style={{ fontSize: "0.85rem" }}>
              {previewTable.trial
                ? `All ${totalRows} rows extracted — the first ${previewTable.trial.limit} are unlocked in your trial download.`
                : totalRows > PREVIEW_PAGE_SIZE
                ? `Showing ${previewRangeStart}–${previewRangeEnd} of ${totalRows} rows`
                : `${totalRows} rows`}
            </span>
            {!loading ? (
              <Row>
                <Col>
                  <div style={{ maxHeight: 320, overflowY: "auto" }}>
                    <Table striped bordered hover className="m-0">
                      <thead
                        style={{
                          position: "sticky",
                          top: 0,
                          zIndex: 1,
                          background: "#fff",
                        }}
                      >
                        <tr>
                          {previewTable.columns.map((column, idx) => (
                            <th key={idx}>{column}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {previewTable.rows
                          .slice(
                            (previewPage - 1) * PREVIEW_PAGE_SIZE,
                            previewPage * PREVIEW_PAGE_SIZE
                          )
                          .map((row, idx) => (
                            <tr key={idx}>
                              {row.map((cell, cellIdx) => (
                                <td
                                  key={cellIdx}
                                  style={
                                    String(cell) === TRIAL_LOCKED_CELL
                                      ? {
                                          filter: "blur(5px)",
                                          userSelect: "none",
                                          pointerEvents: "none",
                                        }
                                      : undefined
                                  }
                                >
                                  {cell}
                                </td>
                              ))}
                            </tr>
                          ))}
                      </tbody>
                    </Table>
                  </div>
                  {totalRows > PREVIEW_PAGE_SIZE && (
                    <div className="d-flex justify-content-center align-items-center mt-3">
                      <LoadingTrackedButton
                        isLoading={false}
                        variant="outline-secondary"
                        size="sm"
                        className="d-flex align-items-center"
                        disabled={previewPage <= 1}
                        onClick={() => setPreviewPage((p) => Math.max(1, p - 1))}
                      >
                        <FontAwesomeIcon icon="chevron-left" className="mr-2" />
                        Previous
                      </LoadingTrackedButton>
                      <span
                        className="text-muted text-center px-3"
                        style={{ fontSize: "0.85rem", minWidth: 110 }}
                      >
                        {`Page ${previewPage} of ${totalPreviewPages}`}
                      </span>
                      <LoadingTrackedButton
                        isLoading={false}
                        variant="outline-secondary"
                        size="sm"
                        className="d-flex align-items-center"
                        disabled={previewPage >= totalPreviewPages}
                        onClick={() =>
                          setPreviewPage((p) =>
                            Math.min(totalPreviewPages, p + 1)
                          )
                        }
                      >
                        Next
                        <FontAwesomeIcon icon="chevron-right" className="ml-2" />
                      </LoadingTrackedButton>
                    </div>
                  )}
                </Col>
              </Row>
            ) : (
              <div>Loading Preview...</div>
            )}
          </>
        )}

      <Row className="justify-content-end mx-1 mt-4">
        <LoadingTrackedButton
          isLoading={loading}
          className={`btn-save-${selectedExportType} m-2`}
          disabled={isCustomFormatStale}
          variant="primary"
          onClick={() => onExport({ preview: false })}
        >
          {`${cta}`}
        </LoadingTrackedButton>
      </Row>
    </div>
  );
};
export default CsvOptions;
